/**
 * Máscaras de documentos e identificadores brasileiros.
 *
 * Mesmo princípio de `telefone.ts`: **a máscara é apresentação e morre na tela**. O
 * input mostra formatado, mas o que sai para o backend (e daí para o banco) é o
 * valor normalizado — só dígitos, salvo a placa, que é alfanumérica. Dois registros
 * só convergem no mesmo nó do grafo se produzirem exatamente a mesma string, então
 * o ponto e a barra do CPF nunca podem entrar no armazenamento.
 *
 * Cada máscara tem o par `formatar` (dígitos → exibição) e `desformatar` (qualquer
 * coisa → normalizado). `formatar` é tolerante a valores parciais: enquanto o
 * usuário digita, mostra o que der para mostrar, sem "pular" caracteres.
 */

import { formatarData } from '@/lib/datas';

export type Formato = 'cpf' | 'cnpj' | 'cpfcnpj' | 'rg' | 'cep' | 'pis' | 'placa' | 'data';

export function apenasDigitos(v: string): string {
  return v.replace(/\D/g, '');
}

function formatarCpf(digitos: string): string {
  const d = digitos.slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatarCnpj(digitos: string): string {
  const d = digitos.slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Escolhe CPF ou CNPJ pelo comprimento: até 11 dígitos é CPF, acima é CNPJ. */
function formatarCpfCnpj(digitos: string): string {
  return digitos.length <= 11 ? formatarCpf(digitos) : formatarCnpj(digitos);
}

function formatarCep(digitos: string): string {
  const d = digitos.slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** PIS/NIS: `XXX.XXXXX.XX-X`. */
function formatarPis(digitos: string): string {
  const d = digitos.slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 8) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8, 10)}-${d.slice(10)}`;
}

/**
 * RG não tem formato nacional único (cada UF emite o seu). Mantemos o valor como o
 * usuário digita — só limpamos espaços nas pontas. Normalizar seria inventar regra.
 */
function formatarRg(valor: string): string {
  return valor.replace(/\s+/g, ' ');
}

/**
 * Placa: antiga `ABC-1234` e Mercosul `ABC1D23`. Uppercase, alfanumérica. O 5º
 * caractere distingue os dois padrões (dígito = antiga, letra = Mercosul), mas só
 * dá para saber com 5+ caracteres; antes disso, mostramos cru em maiúsculas.
 */
function formatarPlaca(valor: string): string {
  const v = valor
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);
  if (v.length <= 3) return v;
  // Mercosul tem letra na 5ª posição; a antiga, dígito — e ganha o hífen.
  const mercosul = v.length >= 5 && /[A-Z]/.test(v[4]);
  if (mercosul) return v;
  return `${v.slice(0, 3)}-${v.slice(3)}`;
}

const FORMATADORES: Record<Formato, (v: string) => string> = {
  cpf: (v) => formatarCpf(apenasDigitos(v)),
  cnpj: (v) => formatarCnpj(apenasDigitos(v)),
  cpfcnpj: (v) => formatarCpfCnpj(apenasDigitos(v)),
  cep: (v) => formatarCep(apenasDigitos(v)),
  pis: (v) => formatarPis(apenasDigitos(v)),
  rg: formatarRg,
  placa: formatarPlaca,
  // Data ISO (YYYY-MM-DD) → pt-BR (DD/MM/AAAA). Só exibição — colunas/leitura, nunca input.
  data: (v) => formatarData(v),
};

/** Dígitos/valor → exibição formatada. Tolerante a valores parciais e a `null`. */
export function formatar(valor: string | null | undefined, formato: Formato): string {
  if (!valor) return '';
  return FORMATADORES[formato](valor);
}

/**
 * Máscara de dinheiro pt-BR (R$), estilo "centavos da direita": o operador digita só dígitos e eles
 * preenchem a partir dos centavos — "350000" → "3.500,00", "1" → "0,01". É apresentação; o valor que
 * sai para o backend é decimal com ponto (`dinheiroParaNumero`).
 */
export function mascaraDinheiro(valor: string): string {
  const digitos = apenasDigitos(valor).replace(/^0+/, '');
  if (!digitos) return '';
  const centavos = digitos.padStart(3, '0');
  const inteiro = centavos.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${inteiro},${centavos.slice(-2)}`;
}

/** Máscara de dinheiro ("3.500,00") → decimal com ponto para a API ("3500.00"). Vazio → "". */
export function dinheiroParaNumero(mascarado: string): string {
  const digitos = apenasDigitos(mascarado);
  if (!digitos) return '';
  const c = digitos.padStart(3, '0');
  return `${c.slice(0, -2)}.${c.slice(-2)}`;
}

/** Valor da API ("3500.00" ou número) → máscara pt-BR ("3.500,00") para carregar no formulário. */
export function numeroParaDinheiro(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '';
  const num = Number(raw);
  if (!Number.isFinite(num)) return '';
  return mascaraDinheiro(String(Math.round(num * 100)));
}

/**
 * Qualquer entrada → valor normalizado para o banco. Placa e RG são alfanuméricos
 * (placa em maiúsculas sem separador); os demais são só dígitos.
 */
export function desformatar(valor: string, formato: Formato): string {
  if (formato === 'placa') return valor.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (formato === 'rg' || formato === 'data') return valor.trim(); // 'data' é só exibição, sem input
  return apenasDigitos(valor);
}
